import { AssetType, THAQAssetType } from "utils/constants/assets.constants";
import { ProjectType } from "utils/constants/project.constants";
import { WorkflowId } from "utils/constants/workflows.constants";

export type TProjectConfiguration = {
  canBeCreatedByHAUsers: boolean;
  canDoDocumentBulkActions: boolean;
  canSubmitDocument: boolean;
  canSponsorShareDocument: boolean;
  hasInterestedPHAs: boolean;
  canViewMemberGroupsAccess: boolean;
  hasReferenceHAOrganization: boolean;
  hasParticipatingHAOrganizations: boolean;
  shouldUseDocumentSubtype: boolean;
  canCreateStructuredDocument: boolean;
};

type TProjectConfigurationByProjectType = Partial<
  Record<ProjectType, TProjectConfiguration>
>;

const projectConfigurations: TProjectConfigurationByProjectType = {
  [ProjectType.Reliance]: {
    canBeCreatedByHAUsers: false,
    canDoDocumentBulkActions: true,
    canSubmitDocument: true,
    canSponsorShareDocument: true,
    hasInterestedPHAs: false,
    canViewMemberGroupsAccess: false,
    hasReferenceHAOrganization: true,
    hasParticipatingHAOrganizations: false,
    shouldUseDocumentSubtype: true,
    canCreateStructuredDocument: false,
  },
  [ProjectType.RegulatoryReview]: {
    canBeCreatedByHAUsers: false,
    canDoDocumentBulkActions: true,
    canSubmitDocument: true,
    canSponsorShareDocument: true,
    hasInterestedPHAs: false,
    canViewMemberGroupsAccess: false,
    hasReferenceHAOrganization: false,
    hasParticipatingHAOrganizations: false,
    shouldUseDocumentSubtype: true,
    canCreateStructuredDocument: false,
  },
  [ProjectType.SharedWorkspace]: {
    canBeCreatedByHAUsers: false,
    canDoDocumentBulkActions: true,
    canSubmitDocument: true,
    canSponsorShareDocument: true,
    hasInterestedPHAs: false,
    canViewMemberGroupsAccess: false,
    hasReferenceHAOrganization: false,
    hasParticipatingHAOrganizations: false,
    shouldUseDocumentSubtype: true,
    canCreateStructuredDocument: true,
  },
};

const projectConfigurationFallback: {
  [K in keyof TProjectConfiguration]: false;
} = {
  canBeCreatedByHAUsers: false,
  canDoDocumentBulkActions: false,
  canSubmitDocument: false,
  canSponsorShareDocument: false,
  hasInterestedPHAs: false,
  canViewMemberGroupsAccess: false,
  hasReferenceHAOrganization: false,
  hasParticipatingHAOrganizations: false,
  shouldUseDocumentSubtype: false,
  canCreateStructuredDocument: false,
};

type TProjectConfigurationKeys = keyof typeof projectConfigurations;

function isProjectTypeExistInConfiguration(
  projectType: ProjectType | undefined | null,
): projectType is TProjectConfigurationKeys {
  return projectType !== undefined && projectType !== null;
}

function isProjectConfiguration(
  configuration: TProjectConfiguration | undefined,
): configuration is TProjectConfiguration {
  return configuration !== undefined;
}

export const deriveProjectConfiguration = (
  projectType: ProjectType | undefined,
) => {
  if (isProjectTypeExistInConfiguration(projectType)) {
    const projectConfiguration = projectConfigurations[projectType];

    return isProjectConfiguration(projectConfiguration)
      ? projectConfiguration
      : projectConfigurationFallback;
  } else {
    return projectConfigurationFallback;
  }
};

const projectConfigurationHAQWorkflow = (
  disableAutoSubmitOfAttachments: boolean,
) => ({
  [ProjectType.Reliance]: {
    [AssetType.Question]: WorkflowId.SubmitImportedHAQ,
    [AssetType.QuestionResponse]: disableAutoSubmitOfAttachments
      ? WorkflowId.SubmitHAQResponseV2
      : WorkflowId.SubmitHAQResponse,
  },
  [ProjectType.RegulatoryReview]: {
    [AssetType.Question]: WorkflowId.SubmitImportedHAQ,
    [AssetType.QuestionResponse]: disableAutoSubmitOfAttachments
      ? WorkflowId.SubmitHAQResponseV2
      : WorkflowId.SubmitHAQResponse,
  },
  [ProjectType.SharedWorkspace]: {
    [AssetType.Question]: WorkflowId.SubmitImportedHAQ,
    [AssetType.QuestionResponse]: disableAutoSubmitOfAttachments
      ? WorkflowId.SubmitHAQResponseV2
      : WorkflowId.SubmitHAQResponse,
  },
});

type TProjectConfigurationHAQWorkflowKeys =
  keyof typeof projectConfigurationHAQWorkflow;

export const deriveHAQWorkFlowId = (
  projectType: ProjectType | undefined,
  HAQType: THAQAssetType | undefined,
  disableAutoSubmitOfAttachments: boolean,
) =>
  projectConfigurationHAQWorkflow(disableAutoSubmitOfAttachments)[
    projectType as TProjectConfigurationHAQWorkflowKeys
  ]?.[HAQType as THAQAssetType];
